import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { fleet, type CandidateAsset, type CommandSelector } from "@/api/client";
import { CompanyFilter } from "@/components/CompanyFilter";
import { Field } from "@/components/Field";
import { Select, SearchInput } from "@/components/inputs";
import { FilePick } from "@/components/FilePick";
import { Icon } from "@/components/icons";
import { assetData } from "@/api/client";
import { COMMANDS, actionClass, toneVar } from "@/modules/fleet/catalogue";

/** New run — FOTA's "New rollout" box, with a command selector on top.
 *
 *  MIRRORED ON PURPOSE (Dattatray: *"Make surr we mirror fota UI as much as
 *  possible with and addtion of commadn selection"*). Same numbered fields,
 *  same "N shown · M selected" line, same tick-box table with a select-all in
 *  the header, same name field and count-labelled create button. An operator
 *  who has rolled firmware already knows this screen.
 *
 *  TWO WAYS TO NAME THE TARGETS, ONE AT A TIME. FOTA has only the filter; a
 *  command run also needs the pasted/CSV list Dattatray asked for. They are a
 *  SEGMENTED CHOICE rather than two live panels, because two populated target
 *  lists mean the button's blast radius depends on which you touched last —
 *  the one-element-two-forms bug this console has shipped before.
 *
 *  The server takes either and runs the same code afterwards, so a ticked run
 *  and a filtered run cannot diverge in what they check.
 */

type Mode = "list" | "bulk";

export function NewRun({ onCreated }: { onCreated: (batchId: string) => void }) {
  const [cmdKey, setCmdKey] = useState("immobilize");
  const spec = COMMANDS.find((c) => c.key === cmdKey) ?? COMMANDS[0];
  const [mode, setMode] = useState<Mode>("list");

  const [company, setCompany] = useState("");
  const [assetType, setAssetType] = useState("");
  const [state, setState] = useState("");
  const [action, setAction] = useState<number>(spec.options[0]?.action ?? 1);
  const [name, setName] = useState("");
  const [wave, setWave] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [text, setText] = useState("");
  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState("");

  const companies = useQuery({
    queryKey: ["fleet", "companies"], queryFn: assetData.companies });
  // WRAPPED, not passed by reference: `assetTypes(company?)` takes an
  // argument, and react-query calls its queryFn with a CONTEXT object — which
  // would arrive as the company filter.
  const types = useQuery({
    queryKey: ["fleet", "asset-types", company],
    queryFn: () => assetData.assetTypes(company || undefined) });

  const selector: CommandSelector = useMemo(() => ({
    ...(company ? { company } : {}),
    ...(assetType ? { asset_type: assetType } : {}),
    ...(state ? { state } : {}),
  }), [company, assetType, state]);

  // THE SAME FILTER the run will use. FOTA's `_candidate_conds` exists so the
  // picker, the count and the batch cannot disagree; this is the client half
  // of that — what is listed here is what the server resolves.
  const cands = useQuery({
    queryKey: ["fleet", "candidates", selector],
    queryFn: () => fleet.candidates(selector),
    enabled: mode === "list",
  });
  const shown: CandidateAsset[] = cands.data?.assets ?? [];
  const total = cands.data?.total ?? 0;

  const pastedIds = text.split(/[\s,]+/).map((x) => x.trim())
    .filter((x) => x && !x.startsWith("#"));
  const targetCount = mode === "list" ? picked.length : pastedIds.length;

  const create = useMutation({
    mutationFn: () => fleet.createBatch({
      ...selector,
      ...(mode === "list" ? { asset_ids: picked } : { asset_ids: pastedIds }),
      name: name.trim() || undefined,
      action: spec.key === "mobilize" ? 1 : action,
      wave_size: wave ? Number(wave) : undefined,
    }),
    onSuccess: (r) => { setPicked([]); setText(""); onCreated(r.batch_id); },
  });

  const onFile = async (f: File | null) => {
    setFileName(f?.name ?? "");
    setParseError("");
    if (!f) return;
    try {
      // BOM first: Excel writes one when it saves a CSV, so the header cell
      // arrives as "﻿asset_id" and would be uploaded as an asset id.
      const body = (await f.text()).replace(/^﻿/, "");
      const rows = body.split(/\r?\n/)
        .map((l) => l.split(",")[0].trim().replace(/^"(.*)"$/, "$1").trim())
        .filter((l) => l && !l.startsWith("#") && l.toLowerCase() !== "asset_id");
      if (rows.length === 0) {
        setParseError("that file has no asset rows in its first column");
        return;
      }
      setText(rows.join("\n"));
    } catch (e) {
      setParseError(e instanceof Error ? e.message : String(e));
    }
  };

  const allShownPicked = shown.length > 0
    && shown.every((a) => picked.includes(a.asset_id));

  return (
    <div className="panel panel-pad">
      {/* 1 · THE COMMAND — the addition to FOTA's box, and it comes first
          because every field under it depends on which command this is. */}
      <Field label="1 · Command">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {COMMANDS.map((c) => (
            <button key={c.key} type="button" disabled={!c.built}
              onClick={() => { setCmdKey(c.key); setAction(c.options[0]?.action ?? 1); }}
              title={c.built ? c.hint : `${c.label} is not built yet`}
              style={{
                font: "inherit", fontSize: 12.5,
                fontWeight: c.key === cmdKey ? 700 : 500,
                padding: "7px 14px", borderRadius: 9,
                cursor: c.built ? "pointer" : "not-allowed",
                opacity: c.built ? 1 : 0.5,
                border: `1px solid ${c.key === cmdKey ? toneVar(c) : "var(--line)"}`,
                background: c.key === cmdKey
                  ? `color-mix(in srgb, ${toneVar(c)} 10%, transparent)`
                  : "transparent",
              }}>
              <Icon name={c.icon} /> {c.label}
            </button>
          ))}
        </div>
      </Field>

      <div style={{ display: "grid", gap: 12, marginTop: 12,
                    gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))" }}>
        {/* THE SHARED FILTER, not a hand-rolled select. It carries `scoped`
            straight from the endpoint that returned the list, so whether an
            "All companies" row is even offered is the SERVER's answer rather
            than a role check in the console — which is the whole reason the
            component exists. */}
        <CompanyFilter label="2 · Owner" width={0}
          value={company}
          onChange={(v) => { setCompany(v); setPicked([]); }}
          companies={companies.data?.companies ?? []}
          scoped={companies.data?.scoped ?? true} />
        <Field label="3 · Asset type">
          <Select className="fill" value={assetType}
            onChange={(e) => { setAssetType(e.target.value); setPicked([]); }}>
            <option value="">Any</option>
            {/* `{asset_type, count}`, not a bare string — and showing the
                COUNT is the point: it tells you how big the filter is before
                you apply it, which is the same service the candidate list
                performs one step later. */}
            {(types.data?.asset_types ?? []).map((t) => (
              <option key={t.asset_type} value={t.asset_type}>
                {t.asset_type} ({t.count})
              </option>
            ))}
          </Select>
        </Field>
        <Field label="4 · Lifecycle state">
          <Select className="fill" value={state}
            onChange={(e) => { setState(e.target.value); setPicked([]); }}>
            <option value="">Any state</option>
            <option value="deployed">deployed</option>
            <option value="inventory">inventory</option>
            <option value="repair">repair</option>
          </Select>
        </Field>
        {/* only for a command that HAS a choice — a release has no type */}
        {spec.options.length > 0 && (
          <Field label={`5 · ${spec.label} as`}>
            <Select className="fill" value={action}
              onChange={(e) => setAction(Number(e.target.value))}>
              {spec.options.map((o) => (
                <option key={o.action} value={o.action}>{o.label}</option>
              ))}
            </Select>
          </Field>
        )}
      </div>

      {/* HOW THE TARGETS ARE NAMED. Exactly one is live. */}
      <div style={{ display: "flex", gap: 8, margin: "14px 0 8px", flexWrap: "wrap" }}>
        {(["list", "bulk"] as Mode[]).map((m) => (
          <button key={m} type="button" onClick={() => setMode(m)}
            style={{
              font: "inherit", fontSize: 12.5, fontWeight: mode === m ? 700 : 500,
              padding: "6px 12px", borderRadius: 9, cursor: "pointer",
              border: `1px solid ${mode === m ? "var(--ok)" : "var(--line)"}`,
              background: mode === m
                ? "color-mix(in srgb, var(--ok) 10%, transparent)" : "transparent",
            }}>
            {m === "list" ? "Choose from list" : "Bulk — paste or CSV"}
          </button>
        ))}
      </div>

      {mode === "list" ? (
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 11.5, color: "var(--t-mute)" }}>
              {shown.length} shown{total > shown.length ? ` of ${total}` : ""} ·{" "}
              <b style={{ color: "var(--t-bright)" }}>{picked.length} selected</b>
            </span>
            {picked.length > 0 && (
              <button className="btn ghost" onClick={() => setPicked([])}>Clear</button>
            )}
            {total > shown.length && (
              <button className="btn ghost"
                title="select every asset the filter covers, not just this page"
                onClick={async () => {
                  const all = await fleet.candidates(selector, 500);
                  setPicked(all.assets.map((a) => a.asset_id));
                }}>
                Select all {total}
              </button>
            )}
          </div>
          <div className="tblwrap" style={{ maxHeight: 240, overflowY: "auto" }}>
            <table className="tbl">
              <thead><tr>
                <th style={{ width: 30 }}>
                  <input type="checkbox" checked={allShownPicked}
                    title="select all shown (filtered)"
                    onChange={(e) => {
                      const ids = shown.map((a) => a.asset_id);
                      setPicked(e.target.checked
                        ? [...new Set([...picked, ...ids])]
                        : picked.filter((id) => !ids.includes(id)));
                    }} />
                </th>
                <th>Asset</th><th>Company</th><th>Type</th><th>State</th>
              </tr></thead>
              <tbody>
                {shown.length === 0 && (
                  <tr><td colSpan={5} className="cap">
                    {cands.isLoading ? "…" : "No asset matches, or none has a telematics box fitted."}
                  </td></tr>
                )}
                {shown.map((a) => (
                  <tr key={a.asset_id}>
                    <td>
                      <input type="checkbox" checked={picked.includes(a.asset_id)}
                        onChange={(e) => setPicked(e.target.checked
                          ? [...picked, a.asset_id]
                          : picked.filter((id) => id !== a.asset_id))} />
                    </td>
                    <td className="mono">{a.asset_id}</td>
                    <td>{a.tenant_id}</td>
                    <td>{a.asset_type}</td>
                    <td>{a.state}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          <div style={{ display: "flex", gap: 12, alignItems: "center",
                        justifyContent: "space-between", flexWrap: "wrap",
                        marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: "var(--t-mute)" }}>
              One asset id per row — paste below, or load a CSV.
            </span>
            <span style={{ display: "flex", gap: 8 }}>
              <a className="btn ghost sm" href={fleet.bulkTemplateUrl(spec.key)}>
                <Icon name="download" /> Template
              </a>
              <FilePick accept=".csv,text/csv" label="Choose .csv"
                fileName={fileName} onFile={onFile} before />
            </span>
          </div>
          <textarea className="inp" rows={5} value={text}
            placeholder={"AE-2W-TEST-VIN005\nAE-2W-TEST-VIN007"}
            onChange={(e) => setText(e.target.value)} />
          {parseError && (
            <div style={{ fontSize: 11.5, color: "var(--bad)" }}>{parseError}</div>
          )}
          <div style={{ fontSize: 11.5, color: "var(--t-mute)", marginTop: 6 }}>
            {pastedIds.length} listed
          </div>
        </>
      )}

      <div style={{ display: "flex", gap: 12, alignItems: "flex-end",
                    justifyContent: "flex-end", flexWrap: "wrap", marginTop: 12 }}>
        <Field label="Run name">
          <SearchInput style={{ width: 220 }} placeholder="e.g. depot 4 arrears"
            value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        {/* FOTA's canary lever, same name, same meaning: how many devices this
            run may touch per tick. Blank = the worker's global budget. */}
        <Field label="Wave size (per tick)">
          <SearchInput style={{ width: 130 }} placeholder="all"
            value={wave} onChange={(e) => setWave(e.target.value.replace(/\D/g, ""))} />
        </Field>
        <button className={actionClass(spec, true)}
          disabled={targetCount === 0 || create.isPending}
          onClick={() => create.mutate()}>
          <Icon name={spec.icon} /> Create run ({targetCount})
        </button>
      </div>
      {create.isError && (
        <div style={{ fontSize: 11.5, color: "var(--bad)", marginTop: 6 }}>
          {String((create.error as Error)?.message ?? "could not create the run")}
        </div>
      )}
      <div style={{ fontSize: 11.5, color: "var(--t-mute)", marginTop: 6 }}>
        A run is created as a <b>draft</b> — no hold is placed and nothing is
        sent until you press Start.
      </div>
    </div>
  );
}
